import { ColliType } from '@frontend/colli/types';
import { Application, Feature } from '@frontend/functionality/types';
import { ExtendedOrder } from '@frontend/order/types';
import { SettingKey } from '@frontend/setting/types';
import { RequirementClient } from '@frontend/stock/api';
import { TransactionType, TransactionUserType } from '@frontend/transaction/types';
import { ScreenTrigger, ScreenType } from '@frontend/wizard/types';
import { useEffect, useState } from 'react';

import { useScreen } from '../useScreen';

interface ViewProps {
    isLoading: boolean;
    hasRequirement: boolean | null;
    hasError: boolean;
    configurationError: boolean;
    canContinue: boolean;
    onContinue: () => void;
}

const useVerifyOrderStockRequirement = (props: { wizard_id: string }): ViewProps => {
    const { store, updateWizardData, onSubmit } = useScreen(props.wizard_id);
    const order = store?.data.order as ExtendedOrder | undefined;
    const [hasRequirement, setHasRequirement] = useState<boolean | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const colliWorkflowId = store?.functionalitySettings.find((setting) => setting.key === SettingKey.COLLI_WORKFLOW_ID)?.value;
    const configurationError = typeof colliWorkflowId !== 'string' || !colliWorkflowId;

    useEffect(() => {
        if (!order?.spot_id || !order.product_id) {
            setHasRequirement(false);
            setHasError(false);
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        setIsLoading(true);
        setHasError(false);
        void RequirementClient.fetchRequirements({ spot_id: order.spot_id, product_id: order.product_id })
            .then((response) => {
                if (!cancelled) setHasRequirement(response.results.length > 0);
            })
            .catch(() => {
                if (!cancelled) setHasError(true);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [order?.product_id, order?.spot_id]);

    const onContinue = () => {
        if (!order || hasRequirement || configurationError) return;

        updateWizardData({
            orderTransaction: true,
            transaction: {
                application: Application.PUDO,
                feature: Feature.ORDER_TRANSACTION,
                type: TransactionType.PUDO,
                user_id: order.user_id,
                spot_id: order.spot_id,
                shipping_notes: `Order: ${order.number ?? order.id}`
            },
            receivers: [
                {
                    user_id: order.user_id,
                    type: TransactionUserType.RECEIVER
                }
            ],
            collis: [
                {
                    label: order.number ?? order.id,
                    index: 1,
                    type: ColliType.STANDARD,
                    workflow_id: colliWorkflowId,
                    spot_id: order.spot_id
                }
            ],
            orderTransactionColliContents: [
                {
                    product_id: order.product_id,
                    quantity: order.quantity
                }
            ]
        });

        const nextStep = store?.steps.find((step) => {
            if (step.screen !== ScreenType.VERIFY_ORDER_STOCK_REQUIREMENT) return false;
            return order.spot_id ? step.trigger === ScreenTrigger.SUBMIT : step.trigger === ScreenTrigger.SUBMIT_FALSE;
        });
        onSubmit(nextStep);
    };

    return {
        isLoading,
        hasRequirement,
        hasError,
        configurationError,
        canContinue: !isLoading && hasRequirement === false && !hasError && !configurationError,
        onContinue
    };
};

export default useVerifyOrderStockRequirement;
